import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import CheckCircleOutlined from '@material-ui/icons/CheckCircleOutlined';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { useNavigate } from 'react-router-dom';
import { useOnboardingContext } from '../OnboardingContext';
import { getOnboardingColors } from '../colors';
import { QuizQuestion } from './quizQuestions';

const useStyles = makeStyles(theme => {
  const c = getOnboardingColors(theme);
  return {
    questionLabel: {
      fontSize: '0.875rem',
      fontWeight: 700,
      color: c.primary,
      marginBottom: theme.spacing(1),
    },
    questionText: {
      fontSize: '1rem',
      fontWeight: 700,
      marginBottom: theme.spacing(2),
    },
    optionButton: {
      display: 'block',
      width: '100%',
      textAlign: 'left',
      textTransform: 'none',
      padding: theme.spacing(1.5, 2),
      marginBottom: theme.spacing(1),
      borderRadius: 8,
      border: `2px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.4,
      cursor: 'pointer',
      '&:hover': {
        borderColor: c.primary,
        backgroundColor: theme.palette.action.hover,
      },
    },
    optionSelected: {
      borderColor: c.primary,
      backgroundColor: `${c.primary}11`,
    },
    optionCorrect: {
      borderColor: c.success,
      backgroundColor: `${c.success}18`,
    },
    optionWrong: {
      borderColor: c.error,
      backgroundColor: `${c.error}18`,
    },
    explanationBox: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(1.5, 2),
      borderRadius: 8,
      backgroundColor: c.warningBannerBg,
    },
    explanationCorrectAnswer: {
      fontWeight: 700,
      fontSize: '0.875rem',
      marginBottom: theme.spacing(0.5),
    },
    explanationText: {
      fontSize: '0.85rem',
      lineHeight: 1.5,
    },
    actions: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: theme.spacing(2, 0),
    },
    backButton: {
      fontWeight: 600,
      textTransform: 'none',
      borderColor: theme.palette.divider,
      borderRadius: 4,
      padding: theme.spacing(1, 2),
    },
    confirmButton: {
      fontWeight: 600,
      textTransform: 'none',
      borderRadius: 4,
      padding: theme.spacing(1, 2),
      color: c.buttonPrimaryFg,
    },
    resultIconSuccess: {
      fontSize: 64,
      color: c.success,
      marginBottom: theme.spacing(1),
    },
    resultIconFail: {
      fontSize: 64,
      color: c.error,
      marginBottom: theme.spacing(1),
    },
    resultScore: {
      fontSize: '2rem',
      fontWeight: 700,
      marginBottom: theme.spacing(0.5),
    },
    resultLabel: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(2),
    },
    resultUnlock: {
      fontSize: '0.875rem',
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(3),
    },
    continueJourneyButton: {
      fontWeight: 700,
      textTransform: 'none',
      borderRadius: 4,
      padding: theme.spacing(1, 2),
      backgroundColor: c.buttonPrimaryBg,
      color: c.buttonPrimaryFg,
      '&:hover': {
        backgroundColor: c.primaryHover,
      },
    },
    retryButton: {
      fontWeight: 700,
      textTransform: 'none',
      borderRadius: 4,
      padding: theme.spacing(1, 2),
      backgroundColor: c.buttonPrimaryBg,
      color: c.buttonPrimaryFg,
      '&:hover': {
        backgroundColor: c.primaryHover,
      },
    },
    reviewButton: {
      fontWeight: 700,
      textTransform: 'none',
      borderRadius: 4,
      padding: theme.spacing(1, 2),
      borderColor: c.primary,
      color: c.primary,
      '&:hover': {
        borderColor: c.primaryHover,
        color: c.primaryHover,
      },
    },
    failedMessage: {
      fontSize: '0.875rem',
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(3),
      lineHeight: 1.5,
    },
  };
});

interface QuizLabels {
  questionOf: string;
  back: string;
  confirm: string;
  continueLabel: string;
  correctAnswer: string;
  quizPassed: string;
  quizFailed: string;
  correctAnswers: string;
  nextJourneyUnlocked: string;
  continueToJourney: string;
  tryAgain: string;
  failedMessage: string;
  reviewJourney: string;
}

interface QuizStepProps {
  questions: QuizQuestion[];
  labels: QuizLabels;
  minCorrect: number;
}

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

/**
 * Fisher-Yates shuffle that returns a new array of indices in random order.
 * E.g. for 4 options → might return [2, 0, 3, 1].
 */
function shuffleIndices(length: number): number[] {
  const arr = Array.from({ length }, (_, i) => i);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function buildShuffleMap(questions: QuizQuestion[]): number[][] {
  return questions.map(q => shuffleIndices(q.options.length));
}

export const QuizStep = ({ questions, labels, minCorrect }: QuizStepProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const oColors = getOnboardingColors(theme);
  const navigate = useNavigate();
  const { session, completeSession, endSession } = useOnboardingContext();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [answers, setAnswers] = useState<(number | null)[]>(
    () => new Array(questions.length).fill(null),
  );
  const [showResults, setShowResults] = useState(false);
  const [shuffleMap, setShuffleMap] = useState<number[][]>(
    () => buildShuffleMap(questions),
  );

  const question = questions[currentIndex];
  const currentShuffle = shuffleMap[currentIndex];
  const total = questions.length;

  /** Maps a shuffled display index back to the original option index */
  const originalIndex = (displayIdx: number) => currentShuffle[displayIdx];
  /** Finds the display index for the original correct answer */
  const correctDisplayIndex = currentShuffle.indexOf(question.correctIndex);

  const handleSelect = (index: number) => {
    if (confirmed) return;
    setSelectedOption(index);
  };

  const handleConfirm = () => {
    if (selectedOption === null) return;
    setConfirmed(true);
    setAnswers(prev => {
      const next = [...prev];
      // Store the original option index for scoring
      next[currentIndex] = originalIndex(selectedOption);
      return next;
    });
  };

  const handleContinue = () => {
    if (currentIndex < total - 1) {
      setCurrentIndex(prev => prev + 1);
      setSelectedOption(null);
      setConfirmed(false);
    } else {
      setShowResults(true);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      const prevIdx = currentIndex - 1;
      const prevOrigAnswer = answers[prevIdx];
      // Find the display index for the previously stored original answer
      const prevDisplayIdx = prevOrigAnswer !== null
        ? shuffleMap[prevIdx].indexOf(prevOrigAnswer)
        : null;
      setCurrentIndex(prevIdx);
      setSelectedOption(prevDisplayIdx);
      setConfirmed(true);
    }
  };

  const handleRetry = () => {
    setCurrentIndex(0);
    setSelectedOption(null);
    setConfirmed(false);
    setAnswers(new Array(questions.length).fill(null));
    setShuffleMap(buildShuffleMap(questions));
    setShowResults(false);
  };

  const handleCompleteQuiz = useCallback(() => {
    const returnPath = session?.returnPath ?? '/onboarding';
    completeSession();
    navigate(returnPath);
  }, [session, completeSession, navigate]);

  const handleReviewJourney = useCallback(() => {
    const returnPath = session?.returnPath ?? '/onboarding';
    endSession();
    navigate(returnPath);
  }, [session, endSession, navigate]);

  const correctCount = answers.reduce<number>(
    (acc, ans, i) => acc + (ans === questions[i].correctIndex ? 1 : 0),
    0,
  );
  const passed = correctCount >= minCorrect;

  // ── Results screen ──
  if (showResults) {
    return (
      <Box style={{ textAlign: 'center', padding: '16px 0' }}>
        {passed ? (
          <CheckCircleOutlined className={classes.resultIconSuccess} />
        ) : (
          <ErrorOutlineIcon className={classes.resultIconFail} />
        )}
        <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 4 }}>
          {passed ? labels.quizPassed : labels.quizFailed}
        </Typography>
        <Typography className={classes.resultScore}>
          {correctCount}/{total}
        </Typography>
        <Typography className={classes.resultLabel}>
          {labels.correctAnswers}
        </Typography>
        {passed ? (
          <>
            <Typography className={classes.resultUnlock}>
              {labels.nextJourneyUnlocked}
            </Typography>
            <Button
              variant="contained"
              className={classes.continueJourneyButton}
              onClick={handleCompleteQuiz}
            >
              {labels.continueToJourney}
            </Button>
          </>
        ) : (
          <>
            <Typography className={classes.failedMessage}>
              {labels.failedMessage
                .replace('{{min}}', String(minCorrect))
                .replace('{{total}}', String(total))}
            </Typography>
            <Box style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <Button
                variant="outlined"
                className={classes.reviewButton}
                onClick={handleReviewJourney}
              >
                {labels.reviewJourney}
              </Button>
              <Button
                variant="contained"
                className={classes.retryButton}
                onClick={handleRetry}
              >
                {labels.tryAgain}
              </Button>
            </Box>
          </>
        )}
      </Box>
    );
  }

  // ── Question screen ──
  const isCorrect = confirmed && selectedOption === correctDisplayIndex;
  const isWrong = confirmed && selectedOption !== correctDisplayIndex;

  return (
    <Box>
      <Typography className={classes.questionLabel}>
        {labels.questionOf
          .replace('{{current}}', String(currentIndex + 1))
          .replace('{{total}}', String(total))}
      </Typography>
      <Typography className={classes.questionText}>
        {question.question}
      </Typography>

      {currentShuffle.map((origIdx, displayIdx) => {
        let extraClass = '';
        if (confirmed) {
          if (displayIdx === correctDisplayIndex) {
            extraClass = classes.optionCorrect;
          } else if (displayIdx === selectedOption) {
            extraClass = classes.optionWrong;
          }
        } else if (displayIdx === selectedOption) {
          extraClass = classes.optionSelected;
        }

        return (
          <Box
            key={displayIdx}
            className={`${classes.optionButton} ${extraClass}`}
            onClick={() => handleSelect(displayIdx)}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') handleSelect(displayIdx);
            }}
          >
            {LETTERS[displayIdx]}. {question.options[origIdx]}
          </Box>
        );
      })}

      {confirmed && isWrong && (
        <Box
          className={classes.explanationBox}
          style={{
            backgroundColor: isCorrect
              ? `${oColors.success}18`
              : `${oColors.warningBannerBg}`,
          }}
        >
          <Typography
            className={classes.explanationCorrectAnswer}
          >
            {labels.correctAnswer.replace(
              '{{letter}}',
              LETTERS[correctDisplayIndex],
            )}
          </Typography>
          <Typography className={classes.explanationText}>
            {question.explanation}
          </Typography>
        </Box>
      )}

      <Box className={classes.actions}>
        {currentIndex > 0 ? (
          <Button
            variant="outlined"
            color="primary"
            className={classes.backButton}
            onClick={handleBack}
          >
            {labels.back}
          </Button>
        ) : (
          <Box />
        )}
        {!confirmed ? (
          <Button
            variant="contained"
            color="primary"
            className={classes.confirmButton}
            onClick={handleConfirm}
            disabled={selectedOption === null}
          >
            {labels.confirm}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            className={classes.confirmButton}
            onClick={handleContinue}
          >
            {labels.continueLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
};
